import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled, { ThemeContext } from "styled-components";
import Dialog from "./Dialog";
import { bytesToSize } from "../utils";
import configs from "../../configs";
import { InfoLabel, Link, makeStyles } from "@fluentui/react-components";
import {
  SelectObject24Filled,
  QuestionCircle20Filled,
  CircleHalfFill20Filled,
  BoxMultiple20Filled,
  LightbulbFilament20Filled,
  FolderZipFilled
} from "@fluentui/react-icons";

const ColoredText = styled.span`
  color: ${props => props.color};
  // font-size: 12px;
  // font-weight: 600;
  // line-height: var(--lineHeightBase300);
`;

const PerformanceItemContainer = styled.li`
  display: flex;
  min-height: 100px;
  border-bottom: 1px solid #444444;
  margin: 4px;
  color: ${props => props.theme.text};
  max-width: 500px;

  &:last-child {
    border-bottom: none !important;
  }

  & > :first-child {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
  }

  & > :last-child {
    display: flex;
    flex: 1;
    padding: 12px;
    padding-right: 0;
    border-bottom: none !important;
    justify-content: space-between;
    align-items: center;

    label {
      align-self: start;
    }
  }

  h5 {
    font-size: 12px;
    font-weight: 600;
    line-height: var(--lineHeightBase300);
  }

  h6 span {
    font-family: var(--fontFamilyBase);
    font-size: var(--fontSizeBase300);
    font-weight: 600;
    line-height: var(--lineHeightBase300);
  }

  a {
    white-space: nowrap;
    color: ${props => props.theme.blue};
  }

  p {
    margin: 0;
    //styleName: Caption 1;
    font-family: var(--fontFamilyBase);
    font-size: var(--fontSizeBase200);
    font-weight: 400;
    line-height: var(--lineHeightBase200);
    color: #616161;
  }
`;

const useStyles = makeStyles({
  title: {
    //styleName: Subtitle 1;
    fontFamily: "var(--fontFamilyBase)!important",
    fontSize: "var(--fontSizeBase500)!important",
    fontWeight: 600,
    lineHeight: "var(--lineHeightBase500)!important"
  },
  infoContainer: {
    "& button": {
      height: "30px"
    }
  },
  icon: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    "& svg": {
      width: "32px!important",
      height: "32px!important"
    }
  },
  info: {
    height: "fit-content",
    color: "var(--colorBrandBackground)!important"
  },
  dialogContent: {
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto"
  }
});

function PerformanceCheckItem({
  score,
  scoreColor,
  title,
  description,
  learnMoreUrl,
  children,
  Icon
}) {
  const styles = useStyles();

  return (
    <PerformanceItemContainer>
      <div className={styles.icon}>
        <ColoredText as="div" color={scoreColor}>
          <Icon size={32} width={32} height={32} />
        </ColoredText>
        <ColoredText as="h5" color={scoreColor}>
          {score}{" "}
        </ColoredText>{" "}
      </div>{" "}
      <InfoLabel
        className={styles.infoContainer}
        infoButton={<QuestionCircle20Filled className={styles.info} />}
        info={
          <>
            <Link target="_blank" href={learnMoreUrl}>
              Learn more
            </Link>
          </>
        }
      >
        <div>
          <h6>
            {" "}
            <span className="title">{title}:</span> {children}{" "}
          </h6>{" "}
          <p>
            {" "}
            {description}{" "}
            {/* <a rel="noopener noreferrer" target="_blank" href={learnMoreUrl}>
            Learn More{" "}
          </a>{" "} */}
          </p>{" "}
        </div>{" "}
      </InfoLabel>
    </PerformanceItemContainer>
  );
}

PerformanceCheckItem.propTypes = {
  score: PropTypes.string.isRequired,
  scoreColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  description: PropTypes.string.isRequired,
  learnMoreUrl: PropTypes.string.isRequired
};

const scoreToValue = {
  Low: 0,
  Medium: 1,
  High: 2
};

export default function PerformanceCheckDialog({ scores, ...rest }) {
  const theme = useContext(ThemeContext);
  const styles = useStyles();

  const scoreToColor = {
    Low: theme.green,
    Medium: theme.yellow,
    High: theme.red
  };

  const texturesScore =
    scoreToValue[scores.textures.largeTexturesScore] >
    scoreToValue[scores.textures.score]
      ? scores.textures.largeTexturesScore
      : scores.textures.score;

  const docsOptimizationUrl = configs.docsOptimizationUrl();

  return (
    <Dialog
      classNameContent={styles.dialogContent}
      classNameTitle={styles.title}
      {...rest}
    >
      <ul>
        <PerformanceCheckItem
          title="Polygon Count"
          description="We recommend your scene use no more than 50,000 triangles for mobile devices."
          learnMoreUrl={`${docsOptimizationUrl}`}
          score={scores.polygons.score}
          scoreColor={scoreToColor[scores.polygons.score]}
          Icon={SelectObject24Filled}
        >
          <ColoredText color={scoreToColor[scores.polygons.score]}>
            {scores.polygons.value.toLocaleString()} Triangles
          </ColoredText>
        </PerformanceCheckItem>
        <PerformanceCheckItem
          title="Materials"
          description="We recommend using no more than 25 unique materials in your scene to reduce draw calls on mobile devices."
          learnMoreUrl={`${docsOptimizationUrl}`}
          score={scores.materials.score}
          scoreColor={scoreToColor[scores.materials.score]}
          Icon={CircleHalfFill20Filled}
        >
          <ColoredText color={scoreToColor[scores.materials.score]}>
            {scores.materials.value} Unique Materials
          </ColoredText>
        </PerformanceCheckItem>
        <PerformanceCheckItem
          title="Textures"
          description="We recommend your textures use no more than 256MB of video RAM for mobile devices. We also recommend against using textures larger than 2048 x 2048."
          learnMoreUrl={`${docsOptimizationUrl}`}
          score={texturesScore}
          scoreColor={scoreToColor[texturesScore]}
          Icon={BoxMultiple20Filled}
        >
          <ColoredText color={scoreToColor[scores.textures.score]}>
            ~{bytesToSize(scores.textures.value)} Video RAM
          </ColoredText>
          ,{" "}
          <ColoredText color={scoreToColor[scores.textures.largeTexturesScore]}>
            {scores.textures.largeTexturesValue} Large Textures
          </ColoredText>{" "}
        </PerformanceCheckItem>{" "}
        <PerformanceCheckItem
          title="Lights"
          description="While dynamic lights are not enabled on mobile devices, we recommend using no more than 3 lights in your scene (excluding ambient and hemisphere lights) for your scene to run on low end PCs."
          learnMoreUrl={`${docsOptimizationUrl}`}
          score={scores.lights.score}
          scoreColor={scoreToColor[scores.lights.score]}
          Icon={LightbulbFilament20Filled}
        >
          <ColoredText color={scoreToColor[scores.lights.score]}>
            {scores.lights.value} Lights
          </ColoredText>
        </PerformanceCheckItem>
        <PerformanceCheckItem
          title="File Size"
          description="We recommend a final file size of no more than 16MB for low bandwidth connections. Reducing the file size will reduce the time it takes to download your scene."
          learnMoreUrl={`${docsOptimizationUrl}`}
          score={scores.fileSize.score}
          scoreColor={scoreToColor[scores.fileSize.score]}
          Icon={FolderZipFilled}
        >
          <ColoredText color={scoreToColor[scores.fileSize.score]}>
            {bytesToSize(scores.fileSize.value)}
          </ColoredText>{" "}
        </PerformanceCheckItem>{" "}
      </ul>{" "}
    </Dialog>
  );
}

PerformanceCheckDialog.propTypes = {
  scores: PropTypes.object.isRequired,
  tag: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string.isRequired
};

PerformanceCheckDialog.defaultProps = {
  tag: "div",
  title: "Health Check",
  confirmLabel: "Continue"
};
